import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CustomToolTip from '../../../CommonComponents/CustomToolTip';
import { RemoteAppSvg } from './Svgs';
import useUrlParams from '../../hooks/useUrlParams';
import useSocket from '../../hooks/useSocket';
import useScenario from '../../hooks/useScenario';
import { getSessionSubsystemDiagram, runSessionSanityTest } from '../../../validate/api/validate.api';
import type { SessionSubsystemDiagram } from '../../../validate/api/validate.api';
import SubsystemDiagramModal from '../../../validate/details/SubsystemDiagramModal';
import { WS_SUBSYSTEM_HEALTH_UPDATED_EVENT } from '../../../../utils/constants';
import { showToastMessage } from '../../../../utils/commonFunctions';

type SocketPayload = Record<string, unknown>;

const SOCKET_EVENT_FIELDS = ['eventType', 'event_type', 'message', 'type', 'event', 'eventName', 'event_name'] as const;
const SOCKET_SESSION_FIELDS = ['sessionId', 'session_id', 'scenarioId', 'scenario_id', 'resourceId', 'resource_id'] as const;
const SOCKET_MISSION_FIELDS = ['missionId', 'mission_id'] as const;

const toSocketPayload = (rawData: unknown): SocketPayload | null => {
  if (typeof rawData !== 'string') return null;

  try {
    const parsed = JSON.parse(rawData);
    if (typeof parsed === 'string') return { eventType: parsed };
    if (parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as SocketPayload;
    }
  } catch {
    const eventType = rawData.trim();
    if (eventType) return { eventType };
  }

  return null;
};

const asRecord = (value: unknown): SocketPayload | null => {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return value as SocketPayload;
  }
  return null;
};

const readSocketField = (
  payload: SocketPayload,
  fields: readonly string[],
): string | null => {
  const sources = [
    payload,
    asRecord(payload.data),
    asRecord(payload.payload),
  ].filter(Boolean) as SocketPayload[];

  for (const source of sources) {
    for (const field of fields) {
      const value = source[field];
      if (typeof value === 'string' && value.trim()) return value.trim();
      if (typeof value === 'number') return String(value);
    }
  }

  return null;
};

type ValidateSubsystemDiagramButtonProps = {
  autoOpenOnMount?: boolean;
};

const ValidateSubsystemDiagramButton = ({
  autoOpenOnMount = false,
}: ValidateSubsystemDiagramButtonProps) => {
  const { mission_id, scenario_id, tab_name } = useUrlParams();
  const { socket } = useSocket();
  const { scenario } = useScenario();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [runSanityLoading, setRunSanityLoading] = useState(false);
  const [diagramData, setDiagramData] = useState<SessionSubsystemDiagram | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | string | Date | null>(null);
  const autoOpenedOnMountRef = useRef(false);
  const mountedRef = useRef(true);
  const runSanityRequestInFlightRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    autoOpenedOnMountRef.current = false;
    setDiagramData(null);
    setLastUpdatedAt(null);
    setOpen(false);
    setLoading(false);
    setRunSanityLoading(false);
    runSanityRequestInFlightRef.current = false;
  }, [mission_id, scenario_id]);

  const refreshDiagram = useCallback(async ({
    clearOnError = false,
    showLoader = false,
  }: {
    clearOnError?: boolean;
    showLoader?: boolean;
  } = {}) => {
    if (!mission_id || !scenario_id) return;

    if (showLoader) setLoading(true);

    try {
      const { data } = await getSessionSubsystemDiagram(mission_id, scenario_id);
      if (!mountedRef.current) return;

      setDiagramData(data);
      setLastUpdatedAt(data?.updatedAt ?? null);
    } catch {
      if (clearOnError && mountedRef.current) setDiagramData(null);
    } finally {
      if (showLoader && mountedRef.current) setLoading(false);
    }
  }, [mission_id, scenario_id]);

  useEffect(() => {
    if (!open) return;
    void refreshDiagram({ clearOnError: true, showLoader: true });
  }, [open, refreshDiagram]);

  useEffect(() => {
    if (!autoOpenOnMount || !mission_id || !scenario_id || autoOpenedOnMountRef.current || !tab_name || tab_name !== 'reports') return;

    autoOpenedOnMountRef.current = true;
    if (!diagramData) setLoading(true);
    setOpen(true);
  }, [autoOpenOnMount, diagramData, mission_id, scenario_id, tab_name]);

  useEffect(() => {
    if (!socket || !mission_id || !scenario_id) return;

    const handleMessage = (event: MessageEvent) => {
      const payload = toSocketPayload(event.data);
      if (!payload) return;

      const eventType = readSocketField(payload, SOCKET_EVENT_FIELDS);
      if (eventType !== WS_SUBSYSTEM_HEALTH_UPDATED_EVENT) return;

      const payloadSessionId = readSocketField(payload, SOCKET_SESSION_FIELDS);
      const payloadMissionId = readSocketField(payload, SOCKET_MISSION_FIELDS);
      if (payloadSessionId && payloadSessionId !== scenario_id) return;
      if (payloadMissionId && payloadMissionId !== mission_id) return;

      void refreshDiagram();
    };

    socket.addEventListener('message', handleMessage);
    return () => socket.removeEventListener('message', handleMessage);
  }, [mission_id, refreshDiagram, scenario_id, socket]);

  const runSanityTestAndRefresh = useCallback(async (sanityTestPlanId: string) => {
    if (!mission_id || !scenario_id || !sanityTestPlanId || runSanityRequestInFlightRef.current) return;

    runSanityRequestInFlightRef.current = true;
    setRunSanityLoading(true);
    try {
      const { apiError } = await runSessionSanityTest(mission_id, scenario_id, sanityTestPlanId);
      if (apiError) {
        showToastMessage('error', apiError);
        return;
      }

      await refreshDiagram();
    } finally {
      runSanityRequestInFlightRef.current = false;
      if (mountedRef.current) setRunSanityLoading(false);
    }
  }, [mission_id, refreshDiagram, scenario_id]);

  const subsystems = useMemo(() => diagramData?.subsystems ?? [], [diagramData?.subsystems]);
  const sessionLabel = diagramData?.sessionName ?? scenario?.scenarioName ?? 'Session';
  const setupLabel = diagramData?.setupName ?? 'Setup';

  const hasUnhealthy = subsystems.some((subsystem) => subsystem.healthStatus === 'UNHEALTHY');
  const hasPending = subsystems.some((subsystem) => subsystem.healthStatus === 'PENDING');
  const buttonStateClass = hasUnhealthy
    ? 'unhealthy'
    : hasPending || loading
      ? 'pending'
      : subsystems.length > 0
        ? 'healthy'
        : '';

  return (
    <>
      <CustomToolTip title="Subsystem health" placement="bottom">
        <button
          className={`validate__subsystem__diagram__button ${buttonStateClass}`}
          type="button"
          aria-label="View subsystem health"
          disabled={!mission_id || !scenario_id}
          onClick={(event) => {
            event.stopPropagation();
            if (!diagramData) setLoading(true);
            setOpen(true);
          }}
        >
          <RemoteAppSvg />
        </button>
      </CustomToolTip>

      <SubsystemDiagramModal
        open={open}
        title={loading ? 'Loading Subsystem Health' : 'Subsystem Health'}
        rootLabel={sessionLabel}
        sessionLabel={sessionLabel}
        setupLabel={setupLabel}
        mode="session"
        subsystems={subsystems}
        loading={loading && subsystems.length === 0}
        sanityExecutionStatus={diagramData?.sanityExecutionStatus}
        sanityTestPlanId={diagramData?.sanityTestPlanId}
        updatedAt={lastUpdatedAt}
        runSanityLoading={runSanityLoading}
        onRunSanityTest={runSanityTestAndRefresh}
        isSessionCompleted={scenario?.status === 'SimulationCompleted'}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

export default ValidateSubsystemDiagramButton;
